/**
 * API 服务层
 * 基于 API 文档 v1.2.0
 * 认证方式：Cookie-based (HttpOnly Cookie)
 */

import axios, { AxiosResponse } from "axios";
import type {
  RegisterRequest,
  LoginRequest,
  User,
  UpdateUserRequest,
  ChangePasswordRequest,
  Session,
  SessionListResponse,
  CreateMessageRequest,
  CreateMessageResponse,
  SessionMessagesResponse,
  SSEEvent,
  MemoryListResponse,
  SearchMemoriesParams,
  PaginationParams,
  HealthResponse,
  MemoryHistoryResponse,
  FeedbackRequest,
  FeedbackResponse,
  WorkflowListResponse,
  TTSManifestResponse,
  TTSMessageRequest,
  TTSSettingsResponse,
  TTSStatusResponse,
} from "@/types";

// ============= 配置 =============

const DEFAULT_API_BASE_URL = "http://localhost:8000/api/v1";

function isLoopbackHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function resolveApiBaseUrl(): string {
  const configuredBaseUrl =
    import.meta.env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL;

  if (typeof window === "undefined") {
    return configuredBaseUrl;
  }

  try {
    const resolvedUrl = new URL(configuredBaseUrl);
    const currentHostname = window.location.hostname;

    if (
      isLoopbackHost(resolvedUrl.hostname) &&
      isLoopbackHost(currentHostname) &&
      resolvedUrl.hostname !== currentHostname
    ) {
      resolvedUrl.hostname = currentHostname;
    }

    return resolvedUrl.toString();
  } catch {
    return configuredBaseUrl;
  }
}

export const API_BASE_URL = resolveApiBaseUrl();
const TTS_REQUEST_TIMEOUT_MS = Number(import.meta.env.VITE_TTS_TIMEOUT_MS || 120000);

function isStreamDebugEnabled(): boolean {
  return (
    typeof window !== "undefined" &&
    window.localStorage.getItem("stream_debug") === "true"
  );
}

function logStreamDebug(...args: unknown[]): void {
  if (isStreamDebugEnabled()) {
    console.log(...args);
  }
}

// ============= Axios 实例 =============

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // 重要: 允许携带 Cookie
});

// 响应拦截器：处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Cookie 过期或无效，清除本地用户信息并跳转到登录
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ============= 认证相关 API =============

export const authAPI = {
  /**
   * 用户注册
   */
  register: (data: RegisterRequest): Promise<AxiosResponse<User>> => {
    return api.post("/auth/register", data);
  },

  /**
   * 用户登录 (Cookie-based)
   * 登录成功后，服务器会在响应中设置 HttpOnly Cookie
   */
  login: async (data: LoginRequest): Promise<AxiosResponse<void>> => {
    const formData = new URLSearchParams();
    formData.append("username", data.username);
    formData.append("password", data.password);

    return api.post("/auth/cookie/login", formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  },

  /**
   * 用户登出 (Cookie-based)
   * 登出成功后，服务器会清除客户端的认证 Cookie
   */
  logout: (): Promise<AxiosResponse<void>> => {
    return api.post("/auth/cookie/logout");
  },
};

// ============= 用户管理 API =============

export const userAPI = {
  /**
   * 获取当前用户信息
   */
  getCurrentUser: (): Promise<AxiosResponse<User>> => {
    return api.get("/users/me");
  },

  /**
   * 更新当前用户信息
   */
  updateCurrentUser: (
    data: UpdateUserRequest,
  ): Promise<AxiosResponse<User>> => {
    return api.patch("/users/me", data);
  },

  changePassword: (
    data: ChangePasswordRequest,
  ): Promise<AxiosResponse<void>> => {
    return api.post("/users/me/change-password", data);
  },
};

// ============= 会话管理 API =============

export const sessionAPI = {
  /**
   * 创建会话
   */
  createSession: (): Promise<AxiosResponse<Session>> => {
    return api.post("/sessions");
  },

  /**
   * 获取会话列表
   */
  getSessions: (
    params?: PaginationParams,
  ): Promise<AxiosResponse<SessionListResponse>> => {
    return api.get("/sessions", { params });
  },

  /**
   * 获取会话详情
   */
  getSession: (sessionId: string): Promise<AxiosResponse<Session>> => {
    return api.get(`/sessions/${sessionId}`);
  },

  /**
   * 删除会话
   */
  deleteSession: (sessionId: string): Promise<AxiosResponse<void>> => {
    return api.delete(`/sessions/${sessionId}`);
  },

  /**
   * 获取会话消息
   */
  getSessionMessages: (
    sessionId: string,
    params?: PaginationParams,
  ): Promise<AxiosResponse<SessionMessagesResponse>> => {
    return api.get(`/sessions/${sessionId}/messages`, { params });
  },
};

// ============= 工作流管理 API =============

export const workflowAPI = {
  /**
   * 获取工作流列表
   */
  getWorkflows: (): Promise<AxiosResponse<WorkflowListResponse>> => {
    return api.get("/workflows");
  },
};

// ============= 私有辅助函数 =============

/**
 * 处理 SSE 流式响应的通用函数
 * @param response Fetch API 响应对象
 * @param onEvent 事件回调函数
 */
async function handleSSEStream(
  response: Response,
  onEvent: (event: SSEEvent) => void | Promise<void>,
): Promise<void> {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new Error("No reader available");
  }

  let buffer = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // 按 \n\n 分割事件（SSE 格式）
      const events = buffer.split("\n\n");

      // 保留最后一个不完整的事件在 buffer 中
      buffer = events.pop() || "";

      for (const event of events) {
        const lines = event.trim().split("\n");
        logStreamDebug("[SSE] Raw event lines:", lines);

        for (const line of lines) {
          // 支持 data: 前缀
          if (line.startsWith("data:")) {
            const jsonStr = line.slice(5).trim();
            if (jsonStr) {
              try {
                const eventData = JSON.parse(jsonStr) as SSEEvent;
                logStreamDebug("[SSE] Received event:", eventData);
                await onEvent(eventData);
              } catch (e) {
                console.error("Failed to parse SSE data:", e, "Line:", line);
              }
            }
          }
        }
      }
    }

    // 处理剩余数据
    if (buffer.trim()) {
      const lines = buffer.trim().split("\n");
      logStreamDebug("[SSE] Remaining buffer lines:", lines);
      for (const line of lines) {
        if (line.startsWith("data:")) {
          const jsonStr = line.slice(5).trim();
          if (jsonStr) {
            try {
              const eventData = JSON.parse(jsonStr) as SSEEvent;
              logStreamDebug("[SSE] Received final event:", eventData);
              await onEvent(eventData);
            } catch (e) {
              console.error("Failed to parse remaining SSE data:", e);
            }
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

async function fetchWithTimeout(
  input: string,
  init: RequestInit,
  timeoutMs = TTS_REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(`TTS 请求超时（>${Math.round(timeoutMs / 1000)} 秒）`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function buildTTSRequestError(
  response: Response,
  fallbackMessage: string,
): Promise<Error> {
  try {
    const payload = (await response.json()) as {
      detail?: string;
      message?: string;
      error?: string;
    };
    const detail =
      payload.detail || payload.message || payload.error || `${fallbackMessage}: ${response.status}`;
    return new Error(detail);
  } catch {
    return new Error(`${fallbackMessage}: ${response.status}`);
  }
}

// ============= 消息管理 API =============

export const messageAPI = {
  /**
   * 创建消息（普通对话）
   */
  createMessage: (
    data: CreateMessageRequest,
  ): Promise<AxiosResponse<CreateMessageResponse>> => {
    return api.post("/messages", data);
  },

  /**
   * 创建流式消息（SSE）
   * 使用 Cookie-based 认证，credentials: 'include' 会自动携带 Cookie
   */
  streamMessage: async (
    data: CreateMessageRequest,
    onEvent: (event: SSEEvent) => void | Promise<void>,
  ): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/messages/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      credentials: "include", // 重要: 携带 Cookie
      body: JSON.stringify(data),
    });

    return handleSSEStream(response, onEvent);
  },

  /**
   * 取消生成（Stop）
   */
  cancelMessage: (data: {
    session_id: string;
    run_id?: string;
  }): Promise<
    AxiosResponse<{ success: boolean; run_id: string | null; message: string }>
  > => {
    return api.post("/messages/cancel", data);
  },

  /**
   * 重新生成回复（Regenerate）- 流式响应
   */
  regenerateMessage: async (
    data: { session_id: string; message_id: string; workflow_id?: string },
    onEvent: (event: SSEEvent) => void | Promise<void>,
  ): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/messages/regenerate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    return handleSSEStream(response, onEvent);
  },

  /**
   * 编辑并重新发送（Edit & Resend）- 流式响应
   */
  editAndResendMessage: async (
    data: {
      session_id: string;
      message_id: string;
      new_content: string;
      workflow_id?: string;
    },
    onEvent: (event: SSEEvent) => void | Promise<void>,
  ): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/messages/edit-and-resend`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    return handleSSEStream(response, onEvent);
  },
};

// ============= TTS API =============

export const ttsAPI = {
  getSettings: async (): Promise<TTSSettingsResponse> => {
    const response = await fetchWithTimeout(`${API_BASE_URL}/tts/settings`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw await buildTTSRequestError(response, "获取 TTS 设置失败");
    }

    return (await response.json()) as TTSSettingsResponse;
  },

  createMessageTTS: async (
    sessionId: string,
    messageId: string,
    data: TTSMessageRequest,
  ): Promise<TTSStatusResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/tts`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw await buildTTSRequestError(response, "创建消息 TTS 失败");
    }

    return (await response.json()) as TTSStatusResponse;
  },

  createMessageTTSSegments: async (
    sessionId: string,
    messageId: string,
    data: TTSMessageRequest,
  ): Promise<TTSManifestResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/tts/segments`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      throw await buildTTSRequestError(response, "创建分段 TTS 失败");
    }

    return (await response.json()) as TTSManifestResponse;
  },

  getMessageTTSStatus: async (
    sessionId: string,
    messageId: string,
    voice: string,
  ): Promise<TTSStatusResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/tts/status?voice=${encodeURIComponent(
        voice,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw await buildTTSRequestError(response, "查询 TTS 状态失败");
    }

    return (await response.json()) as TTSStatusResponse;
  },

  getMessageTTSSegmentsStatus: async (
    sessionId: string,
    messageId: string,
    voice: string,
  ): Promise<TTSManifestResponse> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/tts/segments/status?voice=${encodeURIComponent(
        voice,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw await buildTTSRequestError(response, "查询分段 TTS 状态失败");
    }

    return (await response.json()) as TTSManifestResponse;
  },

  fetchAudioBlob: async (
    sessionId: string,
    messageId: string,
    voice: string,
  ): Promise<Blob> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/tts/audio?voice=${encodeURIComponent(
        voice,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "audio/mpeg",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw await buildTTSRequestError(response, "获取 TTS 音频失败");
    }

    return response.blob();
  },

  fetchSegmentAudioBlob: async (
    sessionId: string,
    messageId: string,
    voice: string,
    index: number,
  ): Promise<Blob> => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/sessions/${sessionId}/messages/${messageId}/tts/segments/${index}?voice=${encodeURIComponent(
        voice,
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "audio/mpeg",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw await buildTTSRequestError(response, "获取分段 TTS 音频失败");
    }

    return response.blob();
  },
};

// ============= 记忆管理 API =============

export const memoryAPI = {
  /**
   * 获取记忆列表
   */
  getMemories: (
    params?: PaginationParams,
  ): Promise<AxiosResponse<MemoryListResponse>> => {
    return api.get("/memories", { params });
  },

  /**
   * 搜索记忆
   */
  searchMemories: (
    params: SearchMemoriesParams,
  ): Promise<AxiosResponse<MemoryListResponse>> => {
    return api.get("/memories/search", { params });
  },

  /**
   * 获取记忆历史
   */
  getMemoryHistory: (
    memoryId: string,
  ): Promise<AxiosResponse<MemoryHistoryResponse>> => {
    return api.get(`/memories/${memoryId}/history`);
  },

  /**
   * 删除记忆
   */
  deleteMemory: (memoryId: string): Promise<AxiosResponse<void>> => {
    return api.delete(`/memories/${memoryId}`);
  },
};

// ============= 反馈管理 API =============

export const feedbackAPI = {
  /**
   * 提交消息反馈
   */
  submit: (
    messageId: string,
    data: FeedbackRequest,
  ): Promise<AxiosResponse<FeedbackResponse>> => {
    return api.put(`/messages/${messageId}/feedback`, data);
  },
};

// ============= 健康检查 API =============

export const healthAPI = {
  /**
   * 健康检查
   */
  check: (): Promise<AxiosResponse<HealthResponse>> => {
    return api.get("/health");
  },
};

// ============= 默认导出 =============

export default api;
